/**
 * The five navigation glyphs, traced from the Today canvas rail (2a, lines 101–105) and the
 * Screens tab bars. Same idiom as `components/watch/icons.tsx`: inline SVG, viewBox 24,
 * 1.9px stroke, `currentColor` so the active/inactive colour is set by the parent.
 *
 * Rendered at 19px in the mobile tab bar and 18px in the desktop rail (design README §8).
 */

interface GlyphProps {
  size?: number;
}

/**
 * Today — a ring. The centre dot appears only when the tab is active: the inactive glyph in the
 * canvas is the bare ring (Screens.dc.html:185), and the dot arriving is what marks "you are
 * here". It is also, quietly, the goal ring on the screen it leads to.
 */
export const IconToday = ({ size = 19, active = false }: GlyphProps & { active?: boolean }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.9}
    aria-hidden
  >
    <circle cx="12" cy="12" r="8" />
    {active && <circle cx="12" cy="12" r="3.2" fill="currentColor" stroke="none" />}
  </svg>
);

/** Watch — the same solid play triangle the transport row and every play button uses. */
export const IconWatch = ({ size = 19 }: GlyphProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M8 5.5L18 12L8 18.5Z" />
  </svg>
);

/** Practice — four dots. Not a pencil: nothing here is homework. */
export const IconPractice = ({ size = 19 }: GlyphProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <circle cx="8" cy="8" r="2.2" />
    <circle cx="16" cy="8" r="2.2" />
    <circle cx="8" cy="16" r="2.2" />
    <circle cx="16" cy="16" r="2.2" />
  </svg>
);

/**
 * Words — the letter ა itself, as text and not an SVG. The canvas draws it this way on purpose:
 * the thing the tab leads to is Georgian, so the glyph is Georgian.
 */
export const IconWords = ({
  size = 15,
  lineHeight = 19,
}: {
  size?: number;
  lineHeight?: number;
}) => (
  <span aria-hidden lang="ka" style={{ fontSize: size, lineHeight: `${lineHeight}px` }}>
    ა
  </span>
);

/** Me — head and shoulders. */
export const IconMe = ({ size = 19 }: GlyphProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.9}
    strokeLinecap="round"
    aria-hidden
  >
    <circle cx="12" cy="9" r="3.4" />
    <path d="M5.5 19a6.5 5.5 0 0 1 13 0" />
  </svg>
);
